import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BasilArrowRight } from "@/components/icons/basil";
import { usePrefersReducedMotion } from "@/components/home/usePrefersReducedMotion";
import { Countdown } from "@/components/renewal/Countdown";
import { eventBanners, type EventTone } from "@/lib/renewal/events";
import { cn } from "@shared/lib/utils";

/**
 * 이벤트 배너 캐러셀 — 웹게임 이벤트 보드(Courtyard 홈 히어로 캐러셀 문법:
 * 슬라이드 N + 하단 도트 + 슬라이드당 CTA 1개). 6초 자동 순환(hover/focus/
 * 탭 숨김/reduced-motion 시 정지), 스와이프는 scroll-snap 네이티브.
 */

const FOCUS =
  "outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/70";

const TONE: Record<EventTone, { panel: string; eyebrow: string }> = {
  gold: {
    panel:
      "border-(--ty-line-gold) bg-[linear-gradient(100deg,rgba(217,185,97,0.14),rgba(217,185,97,0.03)_55%,transparent)]",
    eyebrow: "text-accent",
  },
  rarity: {
    panel:
      "border-[rgba(167,139,250,0.35)] bg-[linear-gradient(100deg,rgba(167,139,250,0.16),rgba(167,139,250,0.04)_55%,transparent)]",
    eyebrow: "text-(--ty-rarity)",
  },
  neutral: { panel: "border-line bg-(--ty-bg-2)", eyebrow: "text-ink-mute" },
};

const ROTATE_MS = 6_000;

export function EventBanners() {
  const reduced = usePrefersReducedMotion();
  const banners = useMemo(() => eventBanners(), []);

  const scrollRef = useRef<HTMLDivElement>(null);
  const indexRef = useRef(0);
  const pausedRef = useRef(false);
  const [index, setIndex] = useState(0);

  const goTo = useCallback(
    (next: number, smooth: boolean) => {
      const el = scrollRef.current;
      if (!el) return;
      const clamped = ((next % banners.length) + banners.length) % banners.length;
      el.scrollTo({ left: clamped * el.clientWidth, behavior: smooth ? "smooth" : "auto" });
    },
    [banners.length],
  );

  // 자동 순환 — 유저가 만지는 동안(hover/focus)·백그라운드 탭·reduced-motion 정지
  useEffect(() => {
    if (reduced || banners.length < 2) return;
    const timer = setInterval(() => {
      if (pausedRef.current || document.hidden) return;
      goTo(indexRef.current + 1, true);
    }, ROTATE_MS);
    return () => clearInterval(timer);
  }, [reduced, banners.length, goTo]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el || el.clientWidth === 0) return;
    const next = Math.max(0, Math.min(banners.length - 1, Math.round(el.scrollLeft / el.clientWidth)));
    indexRef.current = next;
    setIndex(next);
  };

  return (
    <section
      aria-label="Events"
      onPointerEnter={() => {
        pausedRef.current = true;
      }}
      onPointerLeave={() => {
        pausedRef.current = false;
      }}
      onFocusCapture={() => {
        pausedRef.current = true;
      }}
      onBlurCapture={() => {
        pausedRef.current = false;
      }}
    >
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className={cn(
          "flex snap-x snap-mandatory overflow-x-auto rounded-2xl",
          "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        )}
      >
        {banners.map((banner) => {
          const tone = TONE[banner.tone];
          return (
            <Link
              key={banner.id}
              to={banner.to}
              className={cn(
                "relative flex h-28 min-w-full snap-center items-center gap-4 overflow-hidden rounded-2xl border px-5 sm:h-32 sm:px-6",
                tone.panel,
                FOCUS,
              )}
            >
              <span className="relative z-10 flex min-w-0 flex-col gap-1">
                <span
                  className={cn(
                    "flex items-center gap-2 font-mono text-[10px] font-medium uppercase tracking-[0.16em]",
                    tone.eyebrow,
                  )}
                >
                  {banner.eyebrow}
                  {banner.endsAt && (
                    <Countdown
                      to={banner.endsAt}
                      className="rounded-full border border-line px-2 py-0.5 font-mono text-[10px] normal-case tracking-normal tabular-nums text-ink-dim"
                    />
                  )}
                </span>
                <span className="truncate text-lg font-bold leading-tight text-ink sm:text-xl">
                  {banner.title}
                </span>
                <span className="truncate font-mono text-[11px] tabular-nums text-ink-mute">
                  {banner.caption}
                </span>
              </span>

              <span className="relative z-10 ml-auto flex shrink-0 items-center gap-1.5 text-sm font-semibold text-accent">
                <span className="hidden sm:inline">{banner.cta}</span>
                <BasilArrowRight className="size-3.5" aria-hidden />
              </span>

              {banner.art && (
                <img
                  src={banner.art}
                  alt=""
                  aria-hidden
                  loading="lazy"
                  draggable={false}
                  className="absolute right-28 top-1/2 hidden h-[165%] -translate-y-1/2 rotate-6 opacity-70 sm:block [mask-image:linear-gradient(to_left,black_40%,transparent)]"
                />
              )}
            </Link>
          );
        })}
      </div>

      {banners.length > 1 && (
        <div className="mt-2 flex justify-center gap-1.5">
          {banners.map((banner, i) => (
            <button
              key={banner.id}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => goTo(i, !reduced)}
              className={cn(
                "h-1 rounded-full transition-all duration-300",
                i === index ? "w-5 bg-accent" : "w-2.5 bg-white/15 hover:bg-white/30",
              )}
            />
          ))}
        </div>
      )}
    </section>
  );
}
